<template>
  <div>
    <PageHeader :title="title" :subtitle="subtitle" :breadcrumbs="NOTEBOOK_CRUMBS" title-class="sensitive">
      <template #title-suffix>
        <a :href="$router.resolve(notebookPath).href" class="order-first inline-flex items-center text-gray-600 hover:text-gray-900" aria-label="Back to the notebook" @click.prevent="backTo($router, notebookPath)">
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="w-3.5 h-3.5" />
        </a>
      </template>
      <template #actions>
        <a v-if="version" :href="downloadHref" class="btn-secondary-sm" title="Download this published version as .ipynb" @click.prevent="downloadVersion">.ipynb</a>
        <router-link :to="notebookPath" class="btn-secondary-sm">Open notebook</router-link>
      </template>
    </PageHeader>

    <div class="pr-body">
      <LoadingIndicator v-if="loading" message="Loading report..." />
      <div v-else-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900 text-[12px]">{{ error }}</div>
      <div v-if="!loading && !error" class="pr-status">
        <template v-if="published">
          <span class="pr-pill pr-pill-ok">Published</span>
          <span>{{ formatWhen(published.at) }} by <DemoText tag="span" :text="published.by_name" /></span>
          <DemoText v-if="published.note" tag="span" class="pr-muted" :text="`· ${published.note}`" />
          <router-link v-if="versions.length > 1" :to="{ path: $route.path }" class="pr-muted ml-auto">all versions ({{ versions.length }})</router-link>
        </template>
        <template v-else>
          <span class="pr-pill">Draft</span>
          <span class="pr-muted">This is the live notebook, not a published version.</span>
          <span v-if="versions.length" class="ml-auto pr-muted">Published:
            <router-link v-for="v in versions" :key="v.version_id" :to="{ path: $route.path, query: { version: v.version_id } }" class="pr-cell-link ml-2">{{ formatWhen(v.at) }}</router-link>
          </span>
        </template>
      </div>
      <div v-if="!loading && !error && !blocks.length" class="pr-empty">Nothing is in the report yet. In the notebook, tick “In report” on the cells whose output belongs here.</div>
      <template v-if="!loading && !error && blocks.length">
        <article v-for="b in blocks" :key="b.id" class="pr-block">
          <div v-if="b.cell_type === 'markdown'" class="pr-prose sensitive" data-demo-blur v-html="markdown(source(b))" />
          <template v-else>
            <header class="pr-head">
              <h2 class="pr-title">{{ meta(b).label || b.id }}</h2>
              <span v-if="meta(b).last_run && meta(b).last_run.at" class="pr-muted" :title="meta(b).last_run.at">{{ formatWhen(meta(b).last_run.at) }}</span>
              <span v-if="meta(b).last_run && meta(b).last_run.changed" class="pr-changed" title="Differs from the previous run">changed</span>
              <a :href="cellHref(b)" class="pr-cell-link ml-auto" title="Open the cell that produced this">↗ cell {{ b.id }}</a>
            </header>
            <div v-if="!meta(b).last_run" class="pr-muted">This cell has not been run.</div>
            <KernelOutput v-else :outputs="b.outputs || []" />
          </template>
        </article>
      </template>
      <section v-if="!loading && !error && manifest" class="pr-block">
        <header class="pr-head">
          <h2 class="pr-title">Data record</h2>
          <span class="pr-pill" :class="manifest.ok ? 'pr-pill-ok' : 'pr-pill-bad'">{{ manifest.ok ? 'verified' : 'changed' }}</span>
          <span class="pr-muted">{{ published ? 'as frozen when published' : 'checked now against the live notebook' }}</span>
        </header>
        <div v-if="!manifest.inputs.length && !manifest.outputs.length" class="pr-muted">No cell has read or written data yet.</div>
        <table v-else class="ae-table pr-manifest">
          <thead><tr><th>Role</th><th>Source</th><th>Rows</th><th>Identity</th><th>Cells</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="(r, i) in manifestRows" :key="i">
              <td>{{ r.role }}</td>
              <td>
                <template v-if="r.kind === 'frame'"><router-link :to="`/impact/frames/${r.id}`" class="sensitive text-neutral-800" data-demo-blur>{{ r.name }}</router-link> <span class="pr-muted">#{{ r.id }}</span></template>
                <span v-else>{{ r.engine || 'warehouse' }} query</span>
              </td>
              <td class="ae-mono">{{ r.rows == null ? '' : Number(r.rows).toLocaleString() }}</td>
              <td class="ae-mono" :title="r.kind === 'frame' ? r.sha256 : `SQL ${r.sql_sha256} · rows ${r.fingerprint}`">{{ r.kind === 'frame' ? `sha ${String(r.sha256 || '').slice(0, 12)}` : `sql ${String(r.sql_sha256 || '').slice(0, 12)} · rows ${String(r.fingerprint || '').slice(0, 12)}` }}</td>
              <td><a v-for="c in r.cells" :key="c" :href="cellHref({ id: c })" class="pr-cell-link mr-2">{{ c }}</a></td>
              <td><span v-if="r.kind === 'frame'" class="pr-pill" :class="r.status === 'verified' ? 'pr-pill-ok' : 'pr-pill-bad'">{{ r.status }}</span><span v-else class="pr-muted">hashed</span></td>
            </tr>
          </tbody>
        </table>
        <div class="pr-muted mt-2">A saved frame is an immutable file; its sha256 is what the notebook read. A warehouse query is not immutable, so it is recorded by its SQL hash and a fingerprint of the rows it returned. Keep a query's result as a saved frame to put it under custody.</div>
      </section>
      <footer v-if="!loading && !error" class="pr-foot">
        <span class="pr-muted">Every figure above is a cell in this notebook. Follow its link for the code and the run that produced it.</span>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import KernelOutput from './KernelOutput.vue';
import DemoText from '@/components/DemoMode/DemoText.vue';
import '@/components/Beta/agent.css';
import { backTo } from '@/utils/backTo';
import { beginSensitiveEgress } from '@/utils/demoEgress';
import { NOTEBOOK_CRUMBS, apiPath, notebookPath } from './impactNav';

export default {
  name: 'NotebookReport',
  components: { PageHeader, LoadingIndicator, KernelOutput, DemoText },
  props: {
    notebookId: { type: String, required: true },
  },
  data() {
    return { notebook: null, loading: true, error: '', manifest: null, versions: [], NOTEBOOK_CRUMBS };
  },
  computed: {
    version() {
      return String(this.$route.query.version || '');
    },
    published() {
      return (this.notebook && this.notebook.published && this.notebook.published.manifest) ? this.notebook.published : null;
    },
    downloadHref() {
      return `/${apiPath(this.notebookId)}/download/?version=${this.version}`;
    },
    manifestRows() {
      const m = this.manifest || { inputs: [], outputs: [] };
      return [...m.inputs.map((r) => ({ ...r, role: 'read' })), ...m.outputs.map((r) => ({ ...r, role: 'wrote' }))];
    },
    notebookPath() {
      return notebookPath(this.notebookId);
    },
    title() {
      return this.notebook ? (this.notebook.title || 'Untitled notebook') : ' ';
    },
    subtitle() {
      return this.notebook ? ['Report', this.published ? `version ${this.formatWhen(this.published.at)}` : 'draft', this.notebook.owner_name].filter(Boolean).join(' · ') : '';
    },
    blocks() {
      return ((this.notebook && this.notebook.notebook && this.notebook.notebook.cells) || []).filter((c) => this.meta(c).report);
    },
  },
  watch: {
    version() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    backTo,
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const base = `${apiPath(this.notebookId)}/`;
        const [{ data }, { data: versions }] = await Promise.all([
          axios.get(this.version ? `${base}published/${this.version}/` : base),
          axios.get(`${base}published/`),
        ]);
        this.notebook = data;
        this.versions = versions;
        if (this.published) {
          this.manifest = this.published.manifest;
        } else {
          const { data: manifest } = await axios.get(`${base}manifest/`);
          this.manifest = manifest;
        }
        document.title = `${this.title} | Kairon Health`;
      } catch (err) {
        this.error = err?.response?.status === 404 ? 'No notebook or published version with that id on this tenant.' : 'The report could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    downloadVersion() {
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      const a = document.createElement('a');
      a.href = this.downloadHref;
      a.download = `${String(this.title).replace(/[^a-z0-9]+/gi, '_')}_${this.version}.ipynb`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    meta(cell) {
      return (cell.metadata && cell.metadata.kairon) || {};
    },
    source(cell) {
      return Array.isArray(cell.source) ? cell.source.join('') : (cell.source || '');
    },
    markdown(text) {
      return DOMPurify.sanitize(marked.parse(String(text || ''), { breaks: true, gfm: true }));
    },
    cellHref(cell) {
      return `${this.$router.resolve(this.notebookPath).href}#cell-${cell.id}`;
    },
    formatWhen(iso) {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.pr-body { max-width: 980px; margin: 0 auto; padding: 24px 24px 128px; display: flex; flex-direction: column; gap: 20px; }
.pr-empty { background: #fff; border: 1px dashed #d6d2ca; border-radius: 8px; padding: 24px; font-size: 12px; color: #56534d; }
.pr-block { background: #fff; border: 1px solid #e4e0d9; border-radius: 8px; padding: 16px 20px; }
.pr-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.pr-title { font-size: 15px; font-weight: 600; color: #1a1918; margin: 0; }
.pr-muted { color: #8b877f; font-size: 11px; }
.pr-changed { background: #fff1d6; color: #7d5a00; border-radius: 4px; padding: 1px 6px; font-size: 10px; font-weight: 600; }
.pr-cell-link { font-size: 11px; color: #35617f; font-family: 'IBM Plex Mono', monospace; }
.pr-cell-link:hover { text-decoration: underline; }
.pr-prose { font-size: 14px; line-height: 1.6; color: #38352f; }
.pr-prose :deep(h1) { font-size: 22px; font-weight: 600; margin: 4px 0 10px; }
.pr-prose :deep(h2) { font-size: 17px; font-weight: 600; margin: 4px 0 8px; }
.pr-prose :deep(h3) { font-size: 14px; font-weight: 600; margin: 4px 0; }
.pr-prose :deep(p) { margin: 0 0 10px; }
.pr-prose :deep(ul), .pr-prose :deep(ol) { padding-left: 22px; margin: 0 0 10px; }
.pr-foot { padding: 8px 0; }
.pr-status { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #38352f; background: #fff; border: 1px solid #e4e0d9; border-radius: 8px; padding: 10px 16px; }
.pr-pill { background: #eeece7; color: #56534d; border-radius: 4px; padding: 1px 7px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.pr-pill-ok { background: #e3f1e6; color: #1f6b35; }
.pr-pill-bad { background: #fde5e1; color: #a3281a; }
.pr-manifest { width: 100%; font-size: 12px; }
.pr-manifest td, .pr-manifest th { padding: 4px 8px; }
</style>
